import { useCallback, useMemo } from "react"

import { parseAsInteger, useQueryStates } from "nuqs"

type Props = {
  defaultItemsPerPage?: number
  itemsPerPageCounts?: number[]
}

const defaultItemsPerPageCounts = [12, 24, 48, 96]

/**
 * Custom hook for managing pagination state.
 * WHY: Uses `nuqs` query state to keep paging in sync with the URL without router churn.
 */
export const usePaging = <TItem,>({
  defaultItemsPerPage,
  itemsPerPageCounts = defaultItemsPerPageCounts,
}: Props = {}) => {
  const [{ page, count }, setQueryState] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    count: parseAsInteger.withDefault(
      defaultItemsPerPage || itemsPerPageCounts[0],
    ),
  })

  const itemsPerPageOptions = useMemo(() => {
    const items = itemsPerPageCounts
    if (
      defaultItemsPerPage &&
      !itemsPerPageCounts.includes(defaultItemsPerPage)
    ) {
      items.push(defaultItemsPerPage)
    }
    return items
      .filter((item) => item >= (defaultItemsPerPage || 0))
      .sort((a, b) => a - b)
  }, [defaultItemsPerPage, itemsPerPageCounts])

  const setSelectedPage = useCallback(
    (value: number) => {
      setQueryState({ page: value })
    },
    [setQueryState],
  )

  const setItemsPerPage = useCallback(
    (value: number) => {
      setQueryState({ page: null, count: value })
    },
    [setQueryState],
  )

  const paginate = useCallback(
    (items: TItem[]) => {
      const displayFrom = (page - 1) * count
      const displayTo = displayFrom + count
      return items.slice(displayFrom, displayTo)
    },
    [page, count],
  )

  return useMemo(
    () => ({
      itemsPerPage: count,
      selectedPage: page,
      setItemsPerPage,
      setSelectedPage,
      itemsPerPageOptions,
      paginate,
    }),
    [
      count,
      page,
      setItemsPerPage,
      setSelectedPage,
      itemsPerPageOptions,
      paginate,
    ],
  )
}
